import { useGoalPinPortfolioMutation } from "web_myplann/Api/Goals/useGoalPinPortfolioMutation";
import { useGoalUnpinPortfolioMutation } from "web_myplann/Api/Goals/useGoalUnpinPortfolioMutation";
import { ALLOCATION_MAX, ALLOCATION_MIN } from "web_myplann/Goals/constants";
import { getActiveGoalPortfolios } from "web_myplann/Goals/Helpers/getActiveGoalPortfolios";
import {
	type GoalPortfolioWithId,
	hasPortfolioId,
} from "web_myplann/Goals/Helpers/hasPortfolioId";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	type GoalPortfolioRequestDTO,
	GoalPortfolioTypeEnum,
	type UpdateGoalRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";

type PinCandidate = Pick<
	GoalPortfolioListItemResponseDTO,
	"id" | "usagePercent"
>;

type UseGoalPortfolioActionsProps = {
	goalId: number;
	goalDto: GoalDetailResponseDTO | undefined;
	onPinSettled?: () => void;
};

const mapPortfolioToRequest = (
	p: GoalPortfolioRequestDTO | GoalPortfolioWithId,
): GoalPortfolioRequestDTO => ({
	portfolioId: p.portfolioId,
	usagePercent: p.usagePercent,
	type: p.type,
});

const mapUpdateBody = (
	dto: GoalDetailResponseDTO,
	portfolios: GoalPortfolioRequestDTO[],
): UpdateGoalRequestDTO => ({
	name: dto.name,
	investmentHorizonEndDate: dto.targetDate,
	currencyCode: dto.currencyCode,
	oneTimePayment: dto.oneTimePayment,
	periodicPayment: dto.periodicPayment,
	portfolios,
});

export const useGoalPortfolioActions = ({
	goalId,
	goalDto,
	onPinSettled,
}: UseGoalPortfolioActionsProps) => {
	const {
		mutate: pinPortfolio,
		isPending: isPinning,
		variables: pinVariables,
	} = useGoalPinPortfolioMutation();
	const {
		mutate: unpinPortfolio,
		isPending: isUnpinning,
		variables: unpinVariables,
	} = useGoalUnpinPortfolioMutation();
	const { mutate: updateAllocation } = useGoalPinPortfolioMutation();

	const pinningId = isPinning ? pinVariables.portfolioId : null;
	const unpinningId = isUnpinning ? unpinVariables.portfolioId : null;

	const pin = (
		picked: PinCandidate,
		type: GoalPortfolioTypeEnum,
		options?: { onSettled?: () => void },
	) => {
		if (isPinning) return;

		const settle = options?.onSettled ?? onPinSettled;

		if (!goalDto) {
			settle?.();
			return;
		}

		const existing = getActiveGoalPortfolios(goalDto.portfolios ?? []).filter(
			hasPortfolioId,
		);
		const alreadyPinned = existing.some((p) => p.portfolioId === picked.id);
		const isSetAside = type === GoalPortfolioTypeEnum.SetAside;
		const available = Math.max(0, ALLOCATION_MAX - picked.usagePercent);
		if (alreadyPinned || (!isSetAside && available < ALLOCATION_MIN)) {
			settle?.();
			return;
		}

		pinPortfolio(
			{
				id: goalId,
				portfolioId: picked.id,
				body: mapUpdateBody(goalDto, [
					...existing.map(mapPortfolioToRequest),
					{
						portfolioId: picked.id,
						usagePercent: isSetAside ? ALLOCATION_MAX : available,
						type,
					},
				]),
			},
			{ onSettled: () => settle?.() },
		);
	};

	const unpin = (portfolioId: number) => {
		if (!goalDto) return;

		unpinPortfolio({
			id: goalId,
			portfolioId,
			body: mapUpdateBody(
				goalDto,
				getActiveGoalPortfolios(goalDto.portfolios ?? [])
					.filter(hasPortfolioId)
					.filter((p) => p.portfolioId !== portfolioId)
					.map(mapPortfolioToRequest),
			),
		});
	};

	const editAllocation = (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => {
		if (!goalDto) return;

		const updated = getActiveGoalPortfolios(goalDto.portfolios ?? [])
			.filter(hasPortfolioId)
			.map((p) =>
				p.portfolioId === portfolioId
					? { portfolioId: p.portfolioId, usagePercent, type: p.type }
					: mapPortfolioToRequest(p),
			);

		updateAllocation(
			{
				id: goalId,
				portfolioId,
				body: mapUpdateBody(goalDto, updated),
			},
			{ onSettled: () => options?.onSettled?.() },
		);
	};

	return { pin, unpin, editAllocation, pinningId, unpinningId };
};
